import { Component, Input, OnInit } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { BsModalService } from 'ngx-bootstrap/modal';
import { MatchDetailsComponent } from '../match-details/match-details.component';
import { PayerService } from '../../payer.service';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { MatchNewActivePlanModalComponent } from '../../match-new-active-plan-modal/match-new-active-plan-modal.component';
import { ActivePlan, Carrier, PendingPlan } from '../../payer';
import { FlashService } from '../../../components/flash/flash.service';

interface Filter {
  value: any;
}

interface Event {
  first: number;
  rows: number;
  filters: { [key: string]: Filter };
  originalEvent:any;
  value?: {label: string, value: any};
}

@Component({
    selector: 'app-match-pending-plan',
    templateUrl: './match-pending-plan.component.html',
    styleUrls: [
        './match-pending-plan.component.scss',
    ],
    standalone: false
})
export class MatchPendingPlanComponent implements OnInit {
  @Input() pendingPlan: PendingPlan;
  @Input() carriers: Array<Carrier>;
  searchString = '';
  loading = false;
  activePlans: []
  selectedActivePlan: ActivePlan;
  first: number = 0;
  last: number;
  rows:number = 10;
  totalRecords: number;
  pageOffset: number = 0;
  page: number = 1;
  searchCriteria: any[] = [];
  matchDetailsModal: BsModalRef;
  matchNewActivePlanModal: BsModalRef;

  tableConfig = {
    columns: {
      carrier_name: {
        title: 'Carrier name',
      },
      plan_name: {
        title: 'Plan name',
      },
      authorization_contact: {
        title: 'Phone number',
      },
      score: {
        title: 'Score',
      }
    },
  };

  keys = Object.keys(this.tableConfig.columns);
  placeholders = {
    carrier_name: 'Carrier name',
    plan_name: 'Plan name',
    authorization_contact: 'Phone number',
    score: 'Score'
  };

  constructor(
    protected http: HttpClient,
    private modalService: BsModalService,
    private payerService: PayerService,
    private router: Router,
    private flashService: FlashService
  ) { }

  resetPlaceHolder(filterName){
    this.placeholders[filterName] = ' ';
  }
  
  addOrUpdateSearchCriteria(searchTerm, value){
    const existingEntry = this.searchCriteria.find(entry => entry[searchTerm] !== undefined);
      if(existingEntry){
        existingEntry[searchTerm] = value
      }
      else {
        this.searchCriteria.push({[searchTerm]: value});
      }
  }

  appendPagination(event?: Event){
    this.pageOffset = event.first / event.rows;
    if(!this.pageOffset){
      this.pageOffset = 0;
    }
    this.page = this.pageOffset + 1;
    this.addOrUpdateSearchCriteria('page', this.page);
    this.addOrUpdateSearchCriteria('per_page', event.rows ? event.rows : this.rows);
  }

  removeSearchCriteria(filterName){
    const index = this.searchCriteria.findIndex(entry => entry[filterName] !== undefined);
    if (index !== -1) {
      this.searchCriteria.splice(index, 1);
    }
  }

  pageChange(event, filterName?: string) {
    if(event.value === null && filterName){
      this.removeSearchCriteria(filterName)
      this.resetPlaceHolder(filterName);
    }
    this.loadPlans(event);
  }

  processEventFilters(event?: Event){
    // Depending on the filter clicked, the event object will have a different structure
    if (event.filters){
      Object.entries(event.filters).forEach(([key, value]) => {
        if(value?.value !== null){
          this.addOrUpdateSearchCriteria(key, value.value);
        }
      });
    }
    if(event.value){
      this.addOrUpdateSearchCriteria(event.value.label, event.value.value);
    }
  }

  buildURL(event?: Event ){
    let url = `insurance_plans/pending/:id/match-suggestions?`.replace(':id', this.pendingPlan.id.toString());
    this.appendPagination(event);
    this.processEventFilters(event);
    this.searchCriteria.map((criteria) => {
      Object.entries(criteria).forEach(([key, value]) => {
        if(value !== null && value !== undefined && value !== ''){
          url += `&${key}=${value}`;
        }
      })
    })
    return url;
  }

  ngOnDestroy() {
    sessionStorage.setItem('searchCriteriaMatchingPlans', JSON.stringify(this.searchCriteria));
  }

  loadPlans(event?:Event) {
    if (!this.searchCriteria || this.searchCriteria.length === 0) {
      const savedSearchCriteria = sessionStorage.getItem('searchCriteriaMatchingPlans');
      if (savedSearchCriteria) {
          this.searchCriteria = JSON.parse(savedSearchCriteria);
      }
    }
    const url = this.buildURL(event);

    this.http.get(`${environment.apiurl}/${url}`, { observe: 'response' }).subscribe((response: any) => {
      this.totalRecords = response.body.meta['x-total-count'];
      this.activePlans = this.formatActivePlans(response.body.data);
    });
  } 

  ngOnInit() {
    const url = `insurance_plans/pending/:id/match-suggestions`.replace(':id', this.pendingPlan.id.toString());
    this.http.get(`${environment.apiurl}/${url}`, { observe: 'response' }).subscribe((response: any) => {
      this.totalRecords = response.body.meta['x-total-count'];
      this.activePlans = this.formatActivePlans(response.body.data);
    })
  }

  onRowSelect(event) {
    this.matchDetailsModal = this.modalService.show(MatchDetailsComponent, {
      initialState: {
        pendingPlanId: this.pendingPlan.id,
        activePlan: event.data
      }
    });
    this.matchDetailsModal.content.onDone.subscribe((activePlan: ActivePlan) => {
      this.navigateToActivePayer(activePlan);
    });
  }

  openMatchNewActivePlanModal() {
    this.matchNewActivePlanModal = this.modalService.show(MatchNewActivePlanModalComponent, {
      initialState: {
        pendingPlanId: this.pendingPlan.id,
        carrierOptions: this.carriers
      }
    });
    this.matchNewActivePlanModal.content.onDone.subscribe((activePlan: ActivePlan) => {
      this.navigateToActivePayer(activePlan);
    });
  }

  confirmMatch() {
    this.payerService.matchPendingPlan(
      this.pendingPlan.id, { carrier_plan_id: this.selectedActivePlan.id }
    ).subscribe(res => this.navigateToActivePayer(this.selectedActivePlan));
  }

  formatActivePlans = (activePlans) => {
    activePlans.forEach((activePlan) => {
      activePlan['score'] = (parseInt(activePlan['score'], 10) + '%')
      activePlan['plan_name'] = activePlan['name'];
    })
    return activePlans;
  } 

  navigateToActivePayer(activePlan: ActivePlan) {
    this.flashService.add({
      type: 'success',
      message: `${this.pendingPlan.plan_name} has been matched with ${activePlan.name}.`
    });
    this.router.navigate(['/payer/active-plans', activePlan.id], {queryParams: {selectTab: 1}});
  }
}
